export type TPosition = { x: number; y: number };

export type TViewport = { width: number; height: number };

export const MIN_SCALE = 0.5;
export const MAX_SCALE = 3;
export const SCALE_STEP = 0.1;

/** Top gap kept above content that overflows the viewport, so page one never touches the edge. */
export const TOP_PADDING = 8;

const FIT_PADDING = 16;

export const roundScale = (value: number): number => Math.round(Math.max(MIN_SCALE, Math.min(value, MAX_SCALE)) * 10) / 10;

/**
 * Clamps a position so content never drifts outside the viewport.
 * Content smaller than the viewport is locked to centre; larger content is
 * clamped so its edges cannot pull away from the viewport bounds.
 */
export const clampPosition = (position: TPosition, scaledWidth: number, scaledHeight: number, viewport: TViewport): TPosition => {
  const x =
    scaledWidth <= viewport.width ? (viewport.width - scaledWidth) / 2 : Math.max(viewport.width - scaledWidth, Math.min(position.x, 0));

  const y =
    scaledHeight <= viewport.height
      ? (viewport.height - scaledHeight) / 2
      : Math.max(viewport.height - scaledHeight, Math.min(position.y, TOP_PADDING));

  return { x, y };
};

/**
 * "contain" fits both axes (whole page visible — PDF, images).
 * "width" fits the width only, letting content scroll vertically (DOCX: fitting a long
 * document's full height would shrink it to illegibility).
 */
export type TFitMode = "contain" | "width";

/** Fit scale that brings content inside the viewport, never above `maxFitScale`. */
export const computeFitScale = (
  contentWidth: number,
  contentHeight: number,
  viewport: TViewport,
  maxFitScale: number,
  fitMode: TFitMode = "contain",
): number => {
  if (contentWidth <= 0 || contentHeight <= 0) return 1;

  const widthScale = (viewport.width - FIT_PADDING) / contentWidth;
  const fitScale = fitMode === "width" ? widthScale : Math.min(widthScale, (viewport.height - FIT_PADDING) / contentHeight);
  return roundScale(Math.min(fitScale, maxFitScale));
};

export type TWheelIntent = "zoom" | "pan-horizontal" | "pan" | "ignore";

/**
 * Decides what a wheel event should do. A bare wheel always zooms. "ignore" leaves
 * the event alone when a modifier pan has nothing to pan.
 */
export const resolveWheelIntent = (
  event: Pick<WheelEvent, "ctrlKey" | "metaKey" | "shiftKey" | "altKey">,
  scaledWidth: number,
  scaledHeight: number,
  viewport: TViewport,
): TWheelIntent => {
  // Trackpad pinch arrives as a wheel event with ctrlKey set, so this also covers pinch-to-zoom.
  if (event.ctrlKey || event.metaKey) return "zoom";

  if (event.shiftKey) return scaledWidth > viewport.width ? "pan-horizontal" : "ignore";

  if (event.altKey) return scaledHeight > viewport.height ? "pan" : "ignore";

  return "zoom";
};

/**
 * Position that keeps `anchor` (viewport coords) over the same content point across a scale change.
 * `newScale` must already be rounded — deriving position from an unrounded scale makes the
 * anchor drift on every step.
 */
export const anchoredPosition = (anchor: TPosition, position: TPosition, currentScale: number, newScale: number): TPosition => {
  const contentX = (anchor.x - position.x) / currentScale;
  const contentY = (anchor.y - position.y) / currentScale;

  return {
    x: anchor.x - contentX * newScale,
    y: anchor.y - contentY * newScale,
  };
};
